import * as React from 'react'
import { ChangeEvent, FC } from 'react'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import FormControlLabel from '@mui/material/FormControlLabel'
import { useSelector } from 'react-redux'
import { selectCurrentUser } from '@user/store/authorization'
import { selectAllLeadNotes } from '@user/store/leadNotes/slice'
import { selectAllOpportunityNotes } from '@user/store/opportunityNotes/slice'
import { ENTITY_TYPES } from '@sales-center/common'
import { InfoTooltip } from '@sales-center/components'
import useTheme from '@mui/material/styles/useTheme'
import { useTidalThemeActive } from '@user/features/TidalThemeToggle'
import { useD2DKnockEnabled } from '@user/store/tenantFeatures/hooks/useD2DKnockEnabled'
import { useMapViewFeatureEnabled } from '@user/store/tenantFeatures/hooks/useMapViewFeatureEnabled'

interface Props {
  externalLinkId: string
  sortBy: string
  showPestPacNote: boolean
  marginTop?: number
  marginBottom?: number
  onChangeSort: (event: ChangeEvent<HTMLInputElement>) => void
  onChangeShowPestPacNotes: () => void
  showKnockStatusNote?: boolean
  onChangeShowKnockStatusNotes?: () => void
  entityType?: ENTITY_TYPES
}

export const NoteFilters: FC<Props> = (props) => {
  const {
    externalLinkId,
    entityType,
    sortBy,
    showPestPacNote,
    marginTop,
    marginBottom,
    onChangeSort,
    onChangeShowPestPacNotes,
    showKnockStatusNote,
    onChangeShowKnockStatusNotes,
  } = props
  const { externalProduct } = useSelector(selectCurrentUser)
  const theme = useTheme()
  const tidalThemeActive = useTidalThemeActive()
  const d2dKnockEnabled = useD2DKnockEnabled()
  const mapViewFeatureEnabled = useMapViewFeatureEnabled()

  const showKnockStatusToggle = d2dKnockEnabled && mapViewFeatureEnabled

  const leadNotes = useSelector(selectAllLeadNotes)
  const oppNotes = useSelector(selectAllOpportunityNotes)
  let sortDisabled: boolean

  if (entityType === ENTITY_TYPES.Opportunity) {
    sortDisabled = oppNotes.filter((item) => !item.externalLinkId).length === 0
    if (showPestPacNote) {
      sortDisabled = oppNotes.length === 0
    }
  } else {
    sortDisabled = leadNotes.filter((item) => !item.externalLinkId).length === 0
    if (showPestPacNote) {
      sortDisabled = leadNotes.length === 0
    }
  }

  return (
    <Stack
      direction={{ xs: externalLinkId ? 'column' : 'row', sm: 'row' }}
      alignItems={{
        xs: externalLinkId ? 'flex-start' : 'center',
        sm: 'center',
      }}
      width="100%"
      mt={marginTop ?? 2}
      mb={marginBottom ?? 3}
    >
      <TextField
        sx={{
          marginRight: theme.spacing(1.2),
          minWidth: '220px',
          backgroundColor: '#ffffff',
        }}
        value={sortBy}
        onChange={onChangeSort}
        label={'Sort by'}
        placeholder={'Sort by'}
        size={tidalThemeActive ? 'medium' : 'small'}
        select
        disabled={sortDisabled}
        data-test-id="note-sort-select"
      >
        <MenuItem value={'NEW'}>Newest to Oldest</MenuItem>
        <MenuItem value={'OLD'}>Oldest to Newest</MenuItem>
      </TextField>
      {externalLinkId ? (
        <Stack direction="row" alignItems="center">
          <FormControlLabel
            sx={{
              marginBottom: theme.spacing(0),
              marginLeft: theme.spacing(0),
              marginRight: theme.spacing(1),
            }}
            labelPlacement="end"
            data-test-id="note-show-pestpac-switch"
            label={
              <Typography ml={0.6} variant="body2">
                Display Notes from {externalProduct}
              </Typography>
            }
            control={
              <Switch
                size="medium"
                checked={showPestPacNote}
                onChange={onChangeShowPestPacNotes}
                data-test-id="showPestPacNoteSwitch"
              />
            }
          />
          <InfoTooltip
            style={{ color: theme.palette.action.active }}
            fontSize={20}
            infoText={`Notes from ${externalProduct} Service Locations will also display`}
            data-test-id="showPestPacNoteTooltip"
          />
        </Stack>
      ) : null}

      {showKnockStatusToggle && (
        <FormControlLabel
          sx={{
            marginBottom: theme.spacing(0),
            marginLeft: theme.spacing(0),
            marginRight: theme.spacing(1),
          }}
          labelPlacement="end"
          data-test-id="note-show-knock-switch-label"
          label={
            <Typography ml={0.6} variant="body2">
              Display Knock Status Notes
            </Typography>
          }
          control={
            <Switch
              size="medium"
              checked={showKnockStatusNote}
              onChange={onChangeShowKnockStatusNotes}
              data-test-id="showKnockStatusNoteSwitch"
            />
          }
        />
      )}
    </Stack>
  )
}
NoteFilters.displayName = 'NoteFilters'
